import React, { useState } from 'react';

import { ResumeContainer } from './ResumeV2.styles';

import ModalErrorCode from '../Modals/ModalErrorCode/ModalErrorCode';
import { DefaultModal } from '@/components/Modals/DefaultModal/DefaultModal';

import { useAppSelector } from '@/app/hooks';
import { makePostCall } from '@/services/makePostCall';
import { useDispatch } from 'react-redux';
import { setIsResume, setIsOpenResume } from '@/reducers/currentPage';
import { EmailVerification } from '../Modals/EmailVerification/EmailVerification';
import { ResumeApplications } from '../ResumeApplications/ResumeApplications';
import { VerificationCode } from '../VerificationCode/VerificationCode';
import VerifyNewAddressModal from '../Modals/VerifyNewAddressModal/VerifyNewAddressModal';

const infoModal = {
  unableSendEmail: {
    title: 'Unable to Send Email',
    description: 'We were unable to send the verification code to your email.  Start a new application or check your email address and try again.',
    labelButtonLeft: 'Start New Application',
    labelButtonRight: 'Try Another Email',
  },
};

export enum PropertyType {
  Email = 'email',
  Code = 'code-input',
}

const Resume = () => {
  const currentForm = useAppSelector((state) => state.CurrentForm);
  const { value } = useAppSelector((state) => state.currentPage);
  const updatedIsResume = useAppSelector((state) => state.currentPage.isResume);
  const [step, setStep] = useState<number>(0);
  const [formValues, setFormValues] = useState({ email: currentForm?.email || '' });
  const [isUnable, setIsUnable] = useState(false);
  const [infoModalError, setInfoModalError] = useState({ title: '', description: '', labelButtonLeft: '', labelButtonRight: '' });
  const [isOpenEnterCode, setIsOpenEnterCode] = useState(false);
  const [applications, setApplications] = useState<any[]>([]);
  const [canStartNewApp, setCanStartNewApp] = useState(true);
  const [error, setError] = useState<string | unknown>(null);
  const [openErrorModal, setOpenErrorModal] = useState(false);

  let templateConfig = useAppSelector((state) => state.TemplateConfig);
  let loanApplication = useAppSelector((state) => state.LoanApplication);
  const resumeOnly = useAppSelector((state) => state.TemplateConfig?.features?.resume?.config?.resumeOnly ?? false);

  const dispatch = useDispatch();

  const sendCodeToEmail = async () => {
    try {
      const endpoint = '/auth/sendCode';
      const payload = { email: formValues.email };
      const newAction = {
        actionProperties: {
          endpoint,
        },
      };
      await makePostCall(newAction, loanApplication, payload, dispatch, templateConfig);
      setIsOpenEnterCode(true);
      setStep(1);
    } catch (error) {
      console.error('Error sending code to email:', error);
      setError(error);
      setOpenErrorModal(true);
      setInfoModalError(infoModal.unableSendEmail);
      setStep(0);
      setIsUnable(true);
    } finally {
    }
  };

  const closeResume = () => {
    dispatch(setIsResume(false));
    dispatch(setIsOpenResume(false));

    setTimeout(() => {
      if (!updatedIsResume && value !== 'Get Started') {
        window.history.pushState({}, '', '/get-started');
      }
    }, 100);
  };

  const handleStartNewApplication = () => {
    closeResume();
  };

  const handleSetApplications = (apps: any) => {
    let loans: any[] = [];

    if (Array.isArray(apps)) {
      loans = apps.flatMap((item: any) => {
        if (item && typeof item === 'object') {
          if (item.loanId || item.loanNumber) {
            return [item];
          }
          if (Array.isArray(item.loans)) {
            return item.loans;
          }
          if (item.loans && typeof item.loans === 'object' && !Array.isArray(item.loans)) {
            return [item.loans];
          }
        }
        return [];
      });
    } else if (apps && typeof apps === 'object') {
      if (apps.loanId || apps.loanNumber) {
        loans = [apps];
      } else if (Array.isArray(apps.loans)) {
        loans = apps.loans;
      } else if (apps.loans && typeof apps.loans === 'object') {
        loans = [apps.loans];
      }
    }

    setApplications(loans);

    if (!loans || loans.length === 0) {
      setIsOpenEnterCode(false);
      dispatch({
        type: 'CurrentForm/addField',
        payload: { email: formValues.email },
      });
      dispatch({ type: 'CurrentPage/setIsResume', payload: false });
      dispatch({ type: 'CurrentPage/setIsNewApplication', payload: true });
    } else {
      setStep(2);
    }
  };

  const inUseAddresses = applications
    .filter((app) => app.borrowerProperty && !app.isAppCompleted)
    .map((app) => {
      const address = [app.borrowerProperty?.propertyStreetAddress, app.borrowerProperty?.propertyCity, app.borrowerProperty?.propertyState, app.borrowerProperty?.propertyZip]
        .filter(Boolean)
        .join(', ')
        .toLowerCase();

      return address;
    });

  const stepsMap: { [key: number]: JSX.Element | null } = {
    0: (
      <EmailVerification
        sendCodeToEmail={sendCodeToEmail}
        setFormValues={setFormValues}
        formValues={formValues}
        handleStartNewApplication={handleStartNewApplication}
        resumeOnly={resumeOnly}
      />
    ),
    1: isOpenEnterCode
      ? (() => {
          return (
            <VerificationCode
              setIsOpenEnterCode={setIsOpenEnterCode}
              email={formValues?.email}
              setStep={setStep}
              sendCodeToEmail={sendCodeToEmail}
              isOpenEnterCode={isOpenEnterCode}
              isNoApplicationsModalOpen={false}
              setApplications={handleSetApplications}
              setCanStartNewApp={setCanStartNewApp}
            />
          );
        })()
      : null,
    2:
      applications.length > 0
        ? (() => {
            return <ResumeApplications applications={applications} prefillEmail={formValues.email} setStep={setStep} canStartNewApp={canStartNewApp} />;
          })()
        : null,
    3: (
      <VerifyNewAddressModal
        open={true}
        onClose={() => setStep(2)}
        inUseAddresses={inUseAddresses}
        onContinue={(formValues, isMailingAddressSame) => {
          try {
            const initialState = {
              borrowerProperty: {
                propertyStreetAddress: formValues.address,
                propertyStreetAddress2: formValues.suit,
                propertyCity: formValues.city,
                propertyState: formValues.state,
                propertyZip: formValues.zip,
                propertyCountry: 'United States',
              },
            };

            dispatch({ type: 'LoanApplication/addValue', payload: { ...loanApplication, ...initialState } });
            dispatch({ type: 'CurrentForm/resetForm' });
            dispatch({
              type: 'CurrentForm/addField',
              payload: {
                address: formValues.address,
                suit: formValues.suit,
                city: formValues.city,
                state: formValues.state,
                zip: formValues.zip,
                isMailingAddressSame,
              },
            });

            dispatch({ type: 'CurrentPage/setIsResume', payload: false });
            dispatch({ type: 'CurrentPage/setIsNewApplication', payload: true });

            dispatch(setIsResume(false));
            dispatch(setIsOpenResume(false));
          } catch (error) {
            console.error('Error in onContinue:', error);
          }
        }}
      />
    ),
  };

  return (
    <ResumeContainer>
      {isUnable ? (
        <ModalErrorCode
          isUnable={isUnable}
          setIsUnable={setIsUnable}
          title={infoModalError.title}
          description={infoModalError.description}
          labelButtonLeft={infoModalError.labelButtonLeft}
          labelButtonRight={infoModalError.labelButtonRight}
          closeResume={closeResume}
        />
      ) : (
        stepsMap[step]
      )}
      {openErrorModal && error && (
        <DefaultModal
          openModal={openErrorModal}
          setOpenModal={setOpenErrorModal}
          isErrorModal={true}
          infoModal={{
            header: (error as any)?.errorResponse?.response?.error?.appActionTitle || 'Error',
            body: typeof error === 'string' ? error : (error as any)?.message || 'An error occurred',
            button: (error as any)?.errorResponse?.response?.error?.appActionButtonTitle || 'Return to %%lenderName%%',
          }}
          preventBackgroundInteraction={true}
        />
      )}
    </ResumeContainer>
  );
};

export default Resume;
